import React from "react";
import styled from "styled-components";

interface PaginationProps {
  activePage?: number;
  totalPages?: number;
  limit?: number;
  onCurrentPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: sans-serif;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: none;
  background: ${(props) => (props.active ? "#0A56AF" : "#f0f0f0")};
  color: ${(props) => (props.active ? "#fff" : "#000")};
  cursor: pointer;
  border-radius: 4px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  padding: 6px 8px;
`;

const Pagination: React.FC<PaginationProps> = ({
  activePage = 1,
  totalPages = 0,
  limit = 10,
  onCurrentPageChange,
  onLimitChange,
}) => {
  if (totalPages === 0) return null;

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onCurrentPageChange && onCurrentPageChange(page);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const visiblePages = 5; // show 5 pages max at a time

    if (totalPages <= visiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const start = Math.max(2, activePage - 1);
      const end = Math.min(totalPages - 1, activePage + 1);

      pages.push(1); // first page always

      if (start > 2) pages.push("...");

      for (let i = start; i <= end; i++) pages.push(i);

      if (end < totalPages - 1) pages.push("...");

      pages.push(totalPages); // last page always
    }

    return pages;
  };

  return (
    <PaginationContainer>
      <PageButton
        disabled={activePage === 1}
        onClick={() => handlePageChange(activePage - 1)}
      >
        Prev
      </PageButton>

      {getPageNumbers().map((page, idx) =>
        page === "..." ? (
          <Ellipsis key={idx}>...</Ellipsis>
        ) : (
          <PageButton
            key={idx}
            active={page === activePage}
            onClick={() => handlePageChange(page as number)}
          >
            {page}
          </PageButton>
        )
      )}

      <PageButton
        disabled={activePage === totalPages}
        onClick={() => handlePageChange(activePage + 1)}
      >
        Next
      </PageButton>

      {onLimitChange && (
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          style={{ marginLeft: "12px", padding: "4px" }}
        >
          {[5, 10, 20, 50, 100].map((val) => (
            <option key={val} value={val}>
              {val} / page
            </option>
          ))}
        </select>
      )}
    </PaginationContainer>
  );
};
export default Pagination;
